import { ArrowUpRight, ArrowRight, FileText, CalendarDays, Users, Timer, CheckCircle2, Trophy } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal, MaskedLine } from "../components/site/Reveal";
import { Seo } from "../components/site/Seo";
import { PaceCalculator } from "../components/simulation/PaceCalculator";
import { DPS_MEDIA } from "../lib/content";
import { trackEvent } from "../lib/analytics";

const EXPECT = [
  { icon: Timer, title: "The Full Race", body: "All 8 runs and all 8 stations at official distances and weights. Chip-style timing so you leave with a real finish time." },
  { icon: Users, title: "Wave Starts", body: "Athletes launch in staggered waves just like race day — you'll learn how to manage traffic in the roxzone and pace off other racers." },
  { icon: CheckCircle2, title: "Judged Standards", body: "Coaches hold you to official movement standards: full sled crossings, knee-touch lunges, below-parallel wall balls. No soft reps." },
  { icon: CalendarDays, title: "Race-Day Logistics", body: "Transitions, station order, gear staging — rehearse the details that steal minutes from unprepared athletes." },
];

const BRING = [
  "Court/hybrid training shoes with grip",
  "Water + electrolytes (it's long)",
  "Grips or chalk for sled pull & carries",
  "A pacing plan per 1km run",
  "Fuel — gel or carbs at the halfway row",
];

export default function Simulation() {
  return (
    <div data-testid="simulation-page" className="pt-16">
      <Seo
        title="Full HYROX Simulation Dallas | HyroxNinja"
        description="Full HYROX race simulations in Dallas: all 8 runs and 8 stations, doubles waves 11:30 AM–12:30 PM, singles 12:30–1:30 PM, $25 per athlete. Estimate your finish time with the pace calculator."
      />
      <section className="relative overflow-hidden">
        <img src={DPS_MEDIA.bg.simulation} alt="Athletes running on treadmills at Dallas Performance Systems" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#070707]/60 to-[#070707]" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-24 pb-16">
          <p className="eyebrow mb-4">Race-Day Rehearsal</p>
          <h1 className="font-head font-black uppercase tracking-tighter leading-[0.9] text-4xl sm:text-5xl lg:text-6xl mb-6">
            <MaskedLine delay={0.1}>Full HYROX</MaskedLine>
            <MaskedLine delay={0.25} className="text-volt">Simulation</MaskedLine>
          </h1>
          <div className="flex flex-wrap gap-3 mb-8" data-testid="simulation-dates">
            {["SEPT 13", "OCT 25"].map((d) => (
              <span key={d} className="font-head font-black uppercase text-2xl sm:text-3xl border border-volt/50 text-volt px-6 py-3">
                {d}
              </span>
            ))}
          </div>
          <div className="flex flex-wrap gap-x-8 gap-y-2 mb-8 font-mono2 text-[11px] uppercase tracking-[0.2em] text-zinc-300" data-testid="simulation-wave-info">
            <span>Doubles waves · 11:30 AM – 12:30 PM</span>
            <span>Singles waves · 12:30 – 1:30 PM</span>
            <span className="text-volt">$25 per athlete</span>
            <span>Self-seed rating 1–3 at sign-up</span>
          </div>
          <p className="text-zinc-300 max-w-2xl leading-relaxed mb-8">
            Two full-scale race simulations, built as the capstone
            of the 12-Week Hyrox Dallas Program (Aug 28 – Nov 13). This is the
            closest thing to race day without a wristband — and the single
            biggest predictor of how your real race will go.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/events" className="btn-volt" data-testid="simulation-book-button" onClick={() => trackEvent("booking_click", { placement: "simulation_page", service: "simulation" })}>
              Book Full Simulation <ArrowRight size={17} />
            </Link>
            <Link to="/simulation-format" className="btn-ghost" data-testid="simulation-format-link">
              <FileText size={16} /> Simulation Format Guide
            </Link>
            <Link to="/results" className="btn-ghost" data-testid="simulation-leaderboard-link">
              <Trophy size={16} /> Leaderboard
            </Link>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-20">
        <Reveal>
          <p className="eyebrow mb-3">What to Expect</p>
          <h2 className="font-head font-extrabold uppercase tracking-tight text-2xl sm:text-3xl lg:text-4xl mb-12">
            This is not a workout. It's a race.
          </h2>
        </Reveal>
        <div className="grid sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
          {EXPECT.map((e, i) => (
            <Reveal key={e.title} delay={i * 0.06} className="bg-[#0c0c0c] p-8 h-full">
              <div data-testid={`simulation-expect-${i}`}>
                <e.icon className="text-volt mb-4" size={26} />
                <h3 className="font-head font-bold uppercase text-xl mb-2">{e.title}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed">{e.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-20">
        <Reveal>
          <p className="eyebrow mb-3">Plan Your Race</p>
          <h2 className="font-head font-extrabold uppercase tracking-tight text-2xl sm:text-3xl lg:text-4xl mb-4">
            Estimate your finish time
          </h2>
          <p className="text-zinc-400 max-w-2xl leading-relaxed mb-10">
            Set your 1km run pace, pick your level, and tune your station
            splits — the calculator adds your 8 runs, 8 stations and roxzone
            transitions into a projected simulation finish time.
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <PaceCalculator />
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-24 grid lg:grid-cols-2 gap-10">
        <Reveal>
          <div className="border border-white/10 bg-[#0c0c0c] p-8 h-full" data-testid="simulation-program-card">
            <p className="eyebrow mb-3">The Build-Up</p>
            <h3 className="font-head font-extrabold uppercase text-2xl mb-4">12-Week Hyrox Dallas Program</h3>
            <p className="text-zinc-400 text-sm leading-relaxed mb-4">
              Aug 28 – Nov 13. Periodized stages that peak you for simulation
              days and race weekend. Currently in <span className="text-volt font-semibold">STAGE: BASE</span> —
              building aerobic endurance with steady-state runs, rows and skis
              before intensity climbs.
            </p>
            <ul className="text-zinc-300 text-sm space-y-2 mb-6 font-mono2">
              <li>› BASE — Aug 28–Sept 19 · engine & endurance</li>
              <li>› BUILD — station strength under fatigue</li>
              <li>› PEAK — race pace, sims & taper</li>
            </ul>
            <Link to="/program" className="inline-flex items-center gap-2 text-volt font-mono2 text-xs uppercase tracking-[0.2em] hover:gap-3 transition-all" data-testid="simulation-program-link">
              View the full program <ArrowUpRight size={14} />
            </Link>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="border border-white/10 bg-[#0c0c0c] p-8 h-full" data-testid="simulation-bring-card">
            <p className="eyebrow mb-3">Checklist</p>
            <h3 className="font-head font-extrabold uppercase text-2xl mb-4">What to Bring</h3>
            <ul className="space-y-3 mb-6">
              {BRING.map((b) => (
                <li key={b} className="flex items-start gap-3 text-zinc-300 text-sm">
                  <CheckCircle2 size={16} className="text-volt mt-0.5 shrink-0" /> {b}
                </li>
              ))}
            </ul>
            <p className="text-zinc-500 text-xs font-mono2 uppercase tracking-wider">
              Specialty classes & simulations are separate, paid sessions.
            </p>
          </div>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-24 -mt-12">
        <Reveal>
          <div className="border border-white/10 bg-[#0c0c0c] p-7" data-testid="simulation-blog-links">
            <p className="eyebrow mb-2">From the Blog</p>
            <p className="text-zinc-400 text-sm leading-relaxed">
              New to sim racing? Start with{" "}
              <Link to="/blog/what-is-a-hyrox-simulation" className="text-volt hover:text-white transition-colors underline underline-offset-2" data-testid="simulation-blog-link-pillar">
                what a HYROX simulation is and how it works
              </Link>
              , then check{" "}
              <Link to="/blog/dallas-hyrox-simulation-results" className="text-volt hover:text-white transition-colors underline underline-offset-2" data-testid="simulation-blog-link-results">
                the latest Dallas sim results and recaps
              </Link>
              .
            </p>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
